'use client'

import { CommentUser, TComment } from '@udecode/plate-comments'
import { proxy, subscribe, useSnapshot } from 'valtio'
import { CustomWebsocketProvider } from '~/components/YJSDocument/YJSDocumentProvider/CustomWebsocketProvider'
import * as Y from 'yjs'
import cloneDeep from 'lodash/cloneDeep'
import { bind } from 'valtio-yjs'
import { safeYTextToSlateElement } from '~/components/TaloEditor/sanitizeSlateElements'
import { Descendant } from 'slate'
import { isEqual } from 'lodash/fp'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'

export type EvaluationContextState = {
  doc: Y.Doc
  provider: CustomWebsocketProvider
}

export type EvaluationDataTableElement = {
  type: string
  value?: Omit<EvaluationDataTableElement, 'value'>
  props: Record<string, string>
  children?: EvaluationDataTableElement[]
}

export type EvaluationDataTableCell = {
  type: string
  props: Record<string, any>
  children?: EvaluationDataTableCell[]
}

export type EvaluationDataTable =
  | string
  | Record<string, EvaluationDataTableCell>
  | Record<string, EvaluationDataTableCell>[]

export type EvaluationDataTableStore = Record<
  string,
  Record<string, EvaluationDataTable>
>

type GateRecord = {
  type: string
  perc: number
}

export type GateValue = {
  tables: GateRecord
  reports: GateRecord
}

export type EvaluationDataTableSlate = Descendant & {
  type: string
  props: { name: string; type: string }
}

export type EvaluationDataTablesSlate = { children: EvaluationDataTableSlate[] }

// Start: Event Messages State Management
const inProcessUpdates = atom<Set<string>>(new Set([]))
const completedUpdates = atom<Set<string>>(new Set([]))
const failedUpdates = atom<Set<string>>(new Set([]))
const isRestoringAtom = atom<boolean>(false)
const isRepairingAtom = atom<boolean>(false)
const isCollaboratingAtom = atom<boolean>(false)
const recoverRequestedAtom = atom<number>(0)

export function useRequestRecover() {
  const set = useSetAtom(recoverRequestedAtom)
  return () => set((v) => v + 1)
}

export function useRecoverRequested() {
  return useAtomValue(recoverRequestedAtom)
}

export function useIsRestoring() {
  return useAtomValue(isRestoringAtom)
}

export function useSetIsRestoring() {
  return useSetAtom(isRestoringAtom)
}

export function useIsRepairing() {
  return useAtomValue(isRepairingAtom)
}

export function useSetIsRepairing() {
  return useSetAtom(isRepairingAtom)
}

export function useIsCollaborating() {
  return useAtomValue(isCollaboratingAtom)
}

export function useSetIsCollaborating() {
  return useSetAtom(isCollaboratingAtom)
}

export function useInProcessUpdates() {
  return useAtomValue(inProcessUpdates)
}

export function useCompletedUpdates() {
  return useAtomValue(completedUpdates)
}

export function useFailedUpdates() {
  return useAtomValue(failedUpdates)
}

export function useClearFailedUpdates() {
  const setFailedUpdates = useSetAtom(failedUpdates)
  return useCallback(() => setFailedUpdates(new Set([])), [setFailedUpdates])
}

/**
 * Reset all event-related atoms. Call when navigating between evaluations
 * to prevent stale update IDs from the previous evaluation bleeding through.
 */
export function useResetEventAtoms() {
  const setInProcess = useSetAtom(inProcessUpdates)
  const setCompleted = useSetAtom(completedUpdates)
  const setFailed = useSetAtom(failedUpdates)
  const setIsRestoring = useSetAtom(isRestoringAtom)
  const setIsRepairing = useSetAtom(isRepairingAtom)
  const setIsCollaborating = useSetAtom(isCollaboratingAtom)

  return useCallback(() => {
    setInProcess(new Set([]))
    setCompleted(new Set([]))
    setFailed(new Set([]))
    setIsRestoring(false)
    setIsRepairing(false)
    setIsCollaborating(false)
  }, [setInProcess, setCompleted, setFailed, setIsRestoring, setIsRepairing, setIsCollaborating])
}

export function useAddPhantomDeleteFailure() {
  const setFailedUpdates = useSetAtom(failedUpdates)
  return useCallback(() => {
    setFailedUpdates((prev) => new Set(prev).add('phantom_delete_revert'))
  }, [setFailedUpdates])
}

export function useUpdateEventMessageRelatedState() {
  const addOrRemoveInProcessUpdate = useAddOrRemoveInProcessUpdate()
  const setCompletedUpdates = useSetAtom(completedUpdates)
  const setFailedUpdates = useSetAtom(failedUpdates)
  const setInProcessUpdates = useSetAtom(inProcessUpdates)
  const setIsCollaborating = useSetAtom(isCollaboratingAtom)
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updateEventMessageRelatedState = useCallback(
    (message: string) => {
      const [event, id] = parseEventMessage(message)
      switch (event) {
        case 'update_start':
          // A new change is being processed — clear stale failures and show updating
          setFailedUpdates(new Set([]))
          addOrRemoveInProcessUpdate(event, id)
          break
        case 'update_end':
          addOrRemoveInProcessUpdate(event, id)
          setCompletedUpdates((previous) => new Set(previous).add(id))
          // If this update was previously marked as failed (timeout beat the ack),
          // remove it from failed — the server confirmed it succeeded
          setFailedUpdates((previous) => {
            if (previous.has(id)) {
              const next = new Set(previous)
              next.delete(id)
              return next
            }
            return previous
          })
          break
        case 'update_failed':
          // Remove from in-progress (stops the spinner) and record the failure
          addOrRemoveInProcessUpdate('update_end', id)
          setFailedUpdates((previous) => new Set(previous).add(id))
          break
        case 'all_clear':
          // Server confirms all processing is done — clear any stale failures
          // and in-process updates that may have been missed
          setInProcessUpdates(new Set([]))
          setFailedUpdates(new Set([]))
          break
        case 'start_sync':
          // Drift monitor detected real changes — show "Syncing..." (teal)
          setIsCollaborating(true)
          // Safety timeout: auto-clear after 30s if end_sync never arrives
          if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
          syncTimeoutRef.current = setTimeout(() => {
            console.warn('[DriftSync] Safety timeout — end_sync not received in 30s')
            setIsCollaborating(false)
            syncTimeoutRef.current = null
          }, 30_000)
          break
        case 'end_sync':
          // Drift correction complete — clear "Syncing..." state
          if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current)
            syncTimeoutRef.current = null
          }
          setIsCollaborating(false)
          // DriftMonitor confirmed server state is correct — clear stale failures
          setFailedUpdates(new Set([]))
          break
        default:
          break
      }
    },
    [addOrRemoveInProcessUpdate, setCompletedUpdates, setFailedUpdates, setInProcessUpdates, setIsCollaborating]
  )
  return updateEventMessageRelatedState
}


function parseEventMessage(message: string): string[] {
  try {
    return message.replaceAll('"', '').split('|')
  } catch {
    console.warn('Invalid event message received:', message)
    return []
  }
}

function useAddOrRemoveInProcessUpdate() {
  const setUpdates = useSetAtom(inProcessUpdates)
  const setFailed = useSetAtom(failedUpdates)
  const addOrRemoveInProcessUpdate = useCallback(
    (event: 'update_start' | 'update_end', id: string) => {
      if (event === 'update_start') {
        setUpdates((previous) => {
          // Safety timeout: remove the in-process update if update_end/all_clear
          // never arrive. These timeouts are generous because the server sends
          // all_clear after every completed update cycle, which clears failures.
          // 120s if many updates queued, 180s otherwise.
          setTimeout(
            () => {
              setUpdates((prev) => {
                if (prev.has(id)) {
                  const next = new Set(prev)
                  next.delete(id)
                  // Grace period: wait 15s before marking failed to give the server
                  // time to send an all_clear message that will clear it
                  setTimeout(() => {
                    setFailed((prevFailed) => new Set(prevFailed).add(id))
                  }, 15000)
                  console.warn(`[UpdateTimeout] Update ${id} timed out — will mark failed in 15s unless all_clear arrives`)
                  return next
                }
                return prev
              })
            },
            previous.size >= 10 ? 180000 : 210000
          )
          return new Set(previous).add(id)
        })
      } else if (event === 'update_end') {
        setUpdates((previous) => {
          const next = new Set(previous)
          next.delete(id)
          return next
        })
      }
    },
    [setUpdates, setFailed]
  )
  return addOrRemoveInProcessUpdate
}

/**
 * Triggers a callback when the evaluation updates come in and go back to zero.
 * @param onUpdateComplete
 */
export function useOnEvaluationUpdateComplete(onUpdateComplete: () => void) {
  // We need to trigger the callback when the updates fall back to 0
  // A combination of a shouldTriggerCallback flag
  // and inProcessUpdates count can be used to achieve this
  const [shouldTriggerCallback, setShouldTriggerCallback] = useState(false)
  const inProcessUpdates = useInProcessUpdates()

  useEffect(() => {
    // When the updates start, set shouldTriggerCallback flag to true
    // So that the updates can be watched as they go back to 0
    if (inProcessUpdates.size && !shouldTriggerCallback) {
      setShouldTriggerCallback(true)
      return
    }
    // When the updates get back to 0, invoke the callback
    // Also set the shouldTriggerCallback flag back to false for the next round of updates
    if (inProcessUpdates.size === 0 && shouldTriggerCallback) {
      onUpdateComplete()
      setShouldTriggerCallback(false)
      return
    }
  }, [inProcessUpdates, shouldTriggerCallback])
}

// End: Event Messages State Management

export const evaluationStore = proxy({
  kv: proxy<Record<string, string>>({}),
  questionnaire: proxy({
    questions: proxy<Record<string, Y.XmlText[]>>({}),
    kv: proxy<Record<string, string>>({}),
    questionsIds: proxy<Record<string, string[]>>({}),
  }),
  dataTables: proxy<EvaluationDataTableStore>({
    default: {
      name: 'Default',
      header: {},
      data: [],
    },
  }),
  comments: proxy({
    values: proxy<Record<string, TComment>>({}),
    users: proxy<Record<string, CommentUser>>({}),
  }),
  gates: proxy<Record<string, GateValue>>({}),
  changeLogs: proxy<{ children: any[] }>({ children: [] }), // Add changeLogs here
  dataTablesSlate: proxy<EvaluationDataTablesSlate>({
    children: [],
  }),
})

// A map to keep track of yjs to evaluation store bindings
// This will let us keep track of existing bindings and prevent binding over and over again
const yjsBindings = new Map<Y.Map<any> | Y.Array<any>, () => void>()
let unbindChangeLogs: (() => void) | null = null
let unbindDataTablesSlate: (() => void) | null = null
let changeLogsHandler: ((update: Uint8Array, origin: any) => void) | null = null
let dataTablesSlateHandler: ((update: Uint8Array, origin: any) => void) | null =
  null
let isChangeLogsHandlerRegistered = false
let isDataTablesSlateHandlerRegistered = false

// Add an observer to the store
subscribe(evaluationStore, () => {
  console.log('EvaluationStore updated:', {
    tableNames: Object.keys(evaluationStore.dataTables),
    tablesWithData: Object.entries(evaluationStore.dataTables).reduce(
      (acc, [name, table]) => {
        acc[name] = {
          hasHeader: Object.keys(table?.header ?? {}).length > 0,
          dataLength: Array.isArray(table?.data) ? table.data.length : 0,
        }
        return acc
      },
      {}
    ),
    changeLogs: evaluationStore.changeLogs.children.length,
  })
})

type QuestionnaireStore = {
  questions: Record<string, Y.XmlText[]>
  kv: Record<string, string>
  questionsIds: Record<string, string[]>
}

type ChangeLogEntry = {
  type: 'change_logs'
  children: Array<{
    type: string
    children: Array<{
      type: string
      props?: Record<string, any>
      children?: Descendant[]
    }>
  }>
}

type EvaluationStore = {
  kv: Record<string, string>
  questionnaire: QuestionnaireStore
  dataTables: EvaluationDataTableStore
  comments: {
    values: Record<string, TComment>
    users: Record<string, CommentUser>
  }
  gates: Record<string, GateValue>
  changeLogs: ChangeLogEntry
  dataTablesSlate: EvaluationDataTablesSlate
}

const initialEvaluationStore: EvaluationStore = {
  kv: {},
  questionnaire: {
    questions: {},
    kv: {},
    questionsIds: {},
  },
  dataTables: {
    default: {
      name: 'Default',
      header: {},
      data: [],
    },
  },
  comments: {
    values: {},
    users: {},
  },
  gates: {},
  changeLogs: { type: 'change_logs', children: [] },
  dataTablesSlate: { children: [] },
}

export function bindChangeLogsToYDoc(doc: Y.Doc) {
  if (unbindChangeLogs) {
    return unbindChangeLogs
  }

  const changeLogsYText = doc.get('change_logs', Y.XmlText) as Y.XmlText

  if (!changeLogsYText) {
    console.warn('No changeLogs found in YDoc')
    return
  }

  const updateHandler = (_update: Uint8Array, _origin: any) => {
    const slateObject = safeYTextToSlateElement(changeLogsYText).children
    const changeLogs = slateObject.find(
      (el): el is Descendant & ChangeLogEntry =>
        'type' in el && el.type === 'change_logs'
    ) as ChangeLogEntry

    if (!changeLogs) {
      return
    }
    evaluationStore.changeLogs.children = changeLogs.children
  }

  // Clean up any existing handler before registering a new one
  if (isChangeLogsHandlerRegistered && changeLogsHandler) {
    doc.off('update', changeLogsHandler)
    isChangeLogsHandlerRegistered = false
    changeLogsHandler = null
  }

  changeLogsHandler = updateHandler
  updateHandler(new Uint8Array(), null)
  doc.on('update', updateHandler)
  isChangeLogsHandlerRegistered = true

  unbindChangeLogs = () => {
    // Only remove handler if it was actually registered
    if (isChangeLogsHandlerRegistered && changeLogsHandler) {
      doc.off('update', changeLogsHandler)
      isChangeLogsHandlerRegistered = false
      changeLogsHandler = null
    }
    unbindChangeLogs = null
  }
  return unbindChangeLogs
}

/**
 * Extracts the data tables out of the main content
 * and binds them to the evaluation store
 */
export function bindDataTablesSlateToYDoc(doc: Y.Doc) {
  // CRITICAL: Check if binding is actually active on THIS doc
  // Just checking if unbindDataTablesSlate exists isn't enough - it might be from a previous doc
  const contentYText = doc.get('content', Y.XmlText) as Y.XmlText
  const isCurrentlyBound = 
    unbindDataTablesSlate && 
    isDataTablesSlateHandlerRegistered && 
    dataTablesSlateHandler !== null
  
  if (isCurrentlyBound) {
    console.log(
      '🔗 bindDataTablesSlateToYDoc: Already bound to this doc, returning existing unbind',
      {
        docGuid: doc.guid,
        contentLength: contentYText.length,
        isHandlerRegistered: isDataTablesSlateHandlerRegistered,
        hasHandler: dataTablesSlateHandler !== null,
      }
    )
    return unbindDataTablesSlate
  }
  
  // If we have an unbind function but the binding isn't active, clean it up first
  if (unbindDataTablesSlate) {
    console.warn(
      '🔗 bindDataTablesSlateToYDoc: Found stale unbind function, cleaning up before rebinding',
      {
        docGuid: doc.guid,
        isHandlerRegistered: isDataTablesSlateHandlerRegistered,
        hasHandler: dataTablesSlateHandler !== null,
      }
    )
    // Call the old unbind to clean up (it will set unbindDataTablesSlate = null)
    unbindDataTablesSlate()
  }

  // contentYText is already defined above (line 329)
  // doc.get() creates the Y.XmlText if it doesn't exist, so this will always return a Y.XmlText
  // (it might be empty, but it's still a valid Y.XmlText object)
  console.log('🔗 bindDataTablesSlateToYDoc: Binding to content Y.XmlText', {
    contentLength: contentYText.length,
    contentString: contentYText.toString().slice(0, 100),
    timestamp: new Date().toISOString(),
  })

  const updateHandler = () => {
    const slateObject = safeYTextToSlateElement(contentYText)
    console.log('🔗 bindDataTablesSlateToYDoc: Update handler fired', {
      slateChildrenCount: slateObject.children?.length ?? 0,
      contentLength: contentYText.length,
      timestamp: new Date().toISOString(),
    })
    const dataTables = slateObject.children.filter(
      (el) => 'type' in el && el.type === 'table'
    ) as EvaluationDataTableSlate[]
    console.log('🔗 bindDataTablesSlateToYDoc: Filtered dataTables', {
      dataTablesCount: dataTables.length,
      timestamp: new Date().toISOString(),
    })
    if (!dataTables.length) {
      console.log(
        '🔗 bindDataTablesSlateToYDoc: No dataTables found, skipping update'
      )
      return
    }
    // Only update if not deeply equal
    if (!isEqual(evaluationStore.dataTablesSlate.children, dataTables)) {
      console.log(
        '🔗 bindDataTablesSlateToYDoc: Updating evaluationStore.dataTablesSlate',
        {
          dataTablesCount: dataTables.length,
          timestamp: new Date().toISOString(),
        }
      )
      evaluationStore.dataTablesSlate.children = dataTables
    } else {
      console.log(
        '🔗 bindDataTablesSlateToYDoc: DataTables unchanged, skipping update'
      )
    }
  }

  // Clean up any existing handler before registering a new one
  if (isDataTablesSlateHandlerRegistered && dataTablesSlateHandler) {
    contentYText.unobserve(dataTablesSlateHandler as any)
    isDataTablesSlateHandlerRegistered = false
    dataTablesSlateHandler = null
  }

  dataTablesSlateHandler = updateHandler
  // Call immediately to process current state
  updateHandler()
  // Observe the Y.XmlText directly - this fires when the content changes
  contentYText.observe(updateHandler)
  isDataTablesSlateHandlerRegistered = true

  unbindDataTablesSlate = () => {
    // Only remove handler if it was actually registered
    if (isDataTablesSlateHandlerRegistered && dataTablesSlateHandler) {
      contentYText.unobserve(dataTablesSlateHandler as any)
      isDataTablesSlateHandlerRegistered = false
      dataTablesSlateHandler = null
    }
    unbindDataTablesSlate = null
  }
  return unbindDataTablesSlate
}

export const resetEvaluationStore = () => {
  console.log('Resetting evaluation store')
  const resetObj = cloneDeep(initialEvaluationStore)
  Object.keys(resetObj).forEach((key) => {
    evaluationStore[key] = resetObj[key]
  })
  // Clear yjsBindings map
  yjsBindings.forEach((unbind) => unbind())
  yjsBindings.clear()
  unbindChangeLogs?.()
  unbindDataTablesSlate?.()
}

export const pushDataTableRow = (
  tableName: string,
  row: Record<string, EvaluationDataTableCell>
): number => {
  ;(
    evaluationStore.dataTables[tableName].data as Record<
      string,
      EvaluationDataTableCell
    >[]
  ).push(row)
  const currentLength =
    (evaluationStore.dataTables[tableName]?.data.length as number) ?? 1
  return currentLength - 1
}

export const setDataTableRow = (
  tableName: string,
  row: Record<string, EvaluationDataTableCell>,
  index: number
) => {
  ;(
    evaluationStore.dataTables[tableName].data as Record<
      string,
      EvaluationDataTableCell
    >[]
  )[index] = row
}

export function useTableSnapshot(
  tableKey?: string | null,
  tableName?: string | null
) {
  ensureTable(tableKey, tableName)
  return useSnapshot(
    tableKey && evaluationStore.dataTables[tableKey]
      ? evaluationStore.dataTables[tableKey]
      : evaluationStore.dataTables.default
  )
}

export function bindEvaluationStoreTableToYMap(
  tableKey: string,
  tableName: string,
  yMap: Y.Map<EvaluationDataTable>
) {
  ensureTable(tableKey, tableName)
  return bindEvaluationStoreObjectToYObject(
    evaluationStore.dataTables[tableKey],
    yMap
  )
}

export function bindEvaluationStoreObjectToYObject(
  storeObject: any,
  yObject: Y.Map<any> | Y.Array<any>
) {
  if (yjsBindings.has(yObject)) {
    const oldUnbind = yjsBindings.get(yObject)!
    // Safely unbind and remove old entry so we can re-bind to the new storeObject
    oldUnbind()
  }

  // Wrap bind call in try-catch to handle errors gracefully
  // Errors can occur if the Y object is in an invalid state (e.g., during transaction cleanup)
  // A month ago, this was called directly without try-catch, which would throw and break the app
  // Now we catch and handle gracefully - the error was always happening but didn't break things
  // because the app would continue and the binding would work on retry
  let unbind: (() => void) | null = null
  try {
    unbind = bind(storeObject, yObject)
  } catch (error) {
    // Log the error for debugging but don't throw - this allows the app to continue
    // The error might occur if binding happens during Y.js transaction cleanup
    console.warn('Error binding store object to Y object:', {
      error,
      yObjectType:
        yObject instanceof Y.Map
          ? 'Map'
          : yObject instanceof Y.Array
          ? 'Array'
          : 'Unknown',
      yObjectParent: (yObject as any).parent ? 'has parent' : 'no parent',
      timestamp: new Date().toISOString(),
    })
    // Return a no-op unbind function so callers don't break
    const noOpUnbind = () => {
      yjsBindings.delete(yObject)
    }
    yjsBindings.set(yObject, noOpUnbind)
    return noOpUnbind
  }

  // Wrap unbind to handle errors gracefully during React Strict Mode
  const wrappedUnbind = createWrappedUnbind(unbind, yObject)
  yjsBindings.set(yObject, wrappedUnbind)

  return wrappedUnbind
}

function createWrappedUnbind(
  unbind: (() => void) | null,
  yObject: Y.Map<any> | Y.Array<any>
) {
  return () => {
    if (yjsBindings.has(yObject)) {
      try {
        unbind?.()
      } catch (error) {
        // Suppress valtio-yjs errors during React Strict Mode
        // The error is logged by Y.js, not thrown, but we catch any actual thrown errors
        if (
          error instanceof Error &&
          error.message.includes('Tried to remove event handler')
        ) {
          // Silently ignore - this is expected in React Strict Mode
        } else {
          // Log but don't throw - this prevents breaking the app flow
          console.warn('Error during unbind:', error)
        }
      } finally {
        yjsBindings.delete(yObject)
      }
    }
  }
}

function ensureTable(tableKey?: string | null, tableName?: string | null) {
  if (tableKey && !evaluationStore.dataTables[tableKey]) {
    evaluationStore.dataTables[tableKey] = cloneDeep({
      ...initialEvaluationStore.dataTables.default,
      name: tableName || tableKey,
    })
  }
}
